import { assign, isEqual } from 'lodash-es'
import qs from 'qs'
import { mapGetters } from 'vuex'
import isSet from '@/utils/is-set'

const resetPagination = () => ({
  currentPage: 1,
  lastPage: null,
  perPage: 20,
  total: 0,
})

export default {
  data: () => ({
    defaultQuery: {
      currency: 'GBP',
      country: 'GB',
      language: 'en',
      offerType: 'crypto',
      searchTerm: '',
      site: 'www.bitcoinmag.de',
    },
    isInitialPageLoad: false,
    query: {
      params: {
        availableCountries: [],
        coins: [],
        countries: [],
        country: undefined,
        currency: undefined,
        date: undefined,
        dateRange: [],
        language: undefined,
        rules: [],
        offer: undefined,
        offerType: undefined,
        partner: undefined,
        paymentMethods: [],
        property: undefined,
        publishedOnly: false,
        dealsOnly: false,
        site: undefined,
        site_id: undefined,
        stocks: [],
        tags: [],
        template: undefined,
        symbol: [],
        site: undefined,
      },
      searchTerm: undefined,
    },
    queryDefaults: [],
    queryPagination: true,
    querySearch: true,
    pagination: resetPagination(),
  }),
  async created() {
    this.isInitialPageLoad = true
    const { query } = this.parseLocation()
    this.$set(this, 'pagination', {
      ...this.pagination,
      perPage: query.perPage ? Number(query.perPage) : this.pagination.perPage,
      currentPage: query.page ? Number(query.page) : this.pagination.currentPage,
    })
    this.$set(this, 'query', this.prepareQuery(query))
    await this.$nextTick()
    this.isInitialPageLoad = false
  },
  watch: {
    pagination: {
      handler() {
        this.updateQueryString()
      },
      deep: true,
    },
    'query.params': {
      handler() {
        if (this.pagination && !this.isInitialPageLoad) this.pagination.currentPage = 1
        this.updateQueryString()
        this.handleQueryUpdate && this.handleQueryUpdate('params')
      },
      deep: true,
    },
    'query.searchTerm': {
      handler(newVal, oldVal) {
        if (newVal === oldVal) return
        if (this.pagination && !this.isInitialPageLoad) this.pagination.currentPage = 1
        this.updateQueryString()
        this.handleQueryUpdate && this.handleQueryUpdate('search')
      },
      deep: true,
    },
  },
  computed: {
    ...mapGetters('options', ['formattedOption']),
    $_queryHandler_numberOfQueries() {
      const totalParams = Object.values(this.query.params).reduce((total, param) => {
        if (isSet(param)) total += 1
        return total
      }, 0)
      return String(totalParams + (this.query.searchTerm ? 1 : 0))
    },
    $_queryHandler_hasQueryChanged() {
      const strippedDefaultQuery = Object.entries(this.defaultQuery).reduce((acc, [key, value]) => {
        const isQueryDefault = this.queryDefaults.find(k => k === key)
        if (isQueryDefault && isSet(value, true)) acc[key] = String(value)
        return acc
      }, {})
      const strippedCurrentQuery = Object.entries(this.query.params).reduce((acc, [key, value]) => {
        if (isSet(value, true)) acc[key] = String(value)
        return acc
      }, {})
      return !isEqual(strippedDefaultQuery, strippedCurrentQuery) || !!this.query.searchTerm
    },
    queryIsDefined() {
      if (!this.query) return false
      if (!this.query.searchTerm && !this.query.params) return false
      return (
        this.query.searchTerm || Object.values(this.query.params).some(value => value !== null && value !== undefined)
      )
    },
    queryBaseObject() {
      if (!this.queryDefaults || !this.queryDefaults.length) return {}
      return Object.entries(this.defaultQuery).reduce((queryObj, [key, value]) => {
        if (!this.queryDefaults.includes(key)) return queryObj
        return assign({}, queryObj, { [key]: value })
      }, {})
    },
    queryFilters() {
      const {
        params: {
          availableCountries = [],
          coins = [],
          offerType,
          paymentMethods = [],
          publishedOnly,
          source,
          stocks = [],
          tag,
          partner,
          console_roles = [],
          rules = [],
          language,
          country,
          published,
          siteType,
          offer,
          employmentType,
          department,
          entity_type_id,
          symbol,
          status,
          site,
        },
        searchTerm,
      } = this.query

      const preppedFilters = [
        source && ['source', source],
        availableCountries.length && ['restrictedCountriesDoesNotContain', availableCountries],
        coins.length && ['coinsContains', coins],
        offerType && ['type', offerType],
        paymentMethods.length && ['paymentMethodsContains', paymentMethods],
        stocks.length && ['stocksContains', stocks],
        publishedOnly && ['isPublished', true],
        this.searchKey && searchTerm && [`${this.searchKey}Like`, searchTerm],
        source && ['source', source],
        tag && ['tagsContains', tag],
        console_roles.length && ['console_rolesContains', console_roles],
        employmentType && ['employment_type', employmentType],
        department && ['department', department],
        partner && ['partner', partner],
        rules.length && ['typeLikeAny', rules],
        language && ['language', language],
        country && ['country', country],
        siteType && ['siteType', siteType],
        offer && ['offer', offer],
        published != undefined && ['published', published],
        entity_type_id && ['entity_type_id', entity_type_id],
        symbol && ['entity.default.symbol', symbol],
        status && status === 'archived' ? ['archived', true] : status === 'published' && ['isPublished', true],
        site && ['site', site],
      ]
        .filter(Boolean)
        .reduce((filters, [key, value]) => ({ ...filters, [key]: this.handleParamValue(value) }), {})

      return Object.keys(preppedFilters).length ? preppedFilters : undefined
    },
  },
  methods: {
    $_queryHandler_resetFilters() {
      const reset = Object.entries(this.query.params).reduce((acc, [key, value]) => {
        const isQueryDefault = this.queryDefaults.find(k => k === key)
        if (isQueryDefault) acc[key] = this.defaultQuery[key]
        else acc[key] = undefined
        return acc
      }, {})
      this.$set(this, 'query', { params: reset, searchTerm: '' })
    },
    // Deprecated. Use $_queryHandler_onUpdatePagination if possible.
    async $_queryHandler_onUpdateItemsPerPage(perPage) {
      this.pagination.perPage = perPage
      await this.$_queryHandler_onUpdatePage(1)
    },
    // Deprecated. Use $_queryHandler_onUpdatePagination if possible.
    async $_queryHandler_onUpdatePage(page) {
      this.pagination.currentPage = page
      this.handleQueryUpdate && this.handleQueryUpdate()
    },
    async $_queryHandler_onUpdatePagination({ page, itemsPerPage }) {
      if (this.isInitialPageLoad) return

      if (this.pagination.perPage !== itemsPerPage) {
        this.pagination.currentPage = 1
      } else {
        this.pagination.currentPage = page
      }
      this.pagination.perPage = itemsPerPage
      this.handleQueryUpdate && this.handleQueryUpdate()
    },
    buildQueryObject({ searchTerm, page, perPage, ...params }) {
      return {
        params,
        searchTerm,
      }
    },
    convertQueryValueType({ key, value }) {
      if (value === undefined && value === null) return value
      switch (this.queryTypes[key]) {
        case 'number':
          return Number(value)
        case 'boolean':
          return value === 'false' ? false : !!value
        case 'date':
          return new Date(value)
        case 'array':
          if (Array.isArray(value)) return value
          return decodeURIComponent(value).split(',')
        default:
          return value
      }
    },
    decodeQueryString(str, defaultDecoder) {
      if (str.toLowerCase() === 'true') return true
      if (str.toLowerCase() === 'false') return false
      return defaultDecoder(str)
    },
    prepareQuery(query) {
      const filtered = this.filterQueryObject(query)
      const transformed = this.transformQueryObject(filtered)
      return this.buildQueryObject(assign(this.queryBaseObject, transformed))
    },
    updateQueryString() {
      const { baseUrl, query } = this.parseLocation()
      const mergedParams = assign({}, query, this.flattenObject(this.query), {
        page: this.pagination.currentPage,
        perPage: this.pagination.perPage,
      })
      const filteredParams = this.filterQueryObject(mergedParams)
      const queryString = qs.stringify(filteredParams)
      const fullUrl = [baseUrl, queryString].filter(Boolean).join('?')
      window.history.replaceState({}, '', fullUrl)
    },
    flattenObject(obj) {
      return Object.entries(obj).reduce((flatObj, [key, value]) => {
        const howToMerge = typeof value === 'object' && value !== null ? value : { [key]: value }
        return assign({}, flatObj, howToMerge)
      }, {})
    },
    parseLocation() {
      const { host, pathname, protocol, search } = window.location
      return {
        baseUrl: [protocol, '//', host, pathname].join(''),
        query: qs.parse(search, { decoder: this.decodeQueryString, ignoreQueryPrefix: true }),
      }
    },
    handleParamValue(param) {
      if (Array.isArray(param) && param.length) return param.join(',')
      return [0, false].includes(param) ? param : param || undefined
    },
    filterQueryObject(queryObject) {
      const fullQueryObject = assign({}, this.flattenObject(this.queryBaseObject), queryObject)
      const queriesToDefine = (this.queriesDefined || [])
        .concat(this.queryPagination ? ['page', 'perPage'] : [])
        .concat(this.querySearch ? ['searchTerm'] : [])
      return Object.entries(fullQueryObject).reduce((queries, [key, param]) => {
        queries[key] = queriesToDefine.includes(key) ? this.handleParamValue(param) : undefined
        return queries
      }, {})
    },
    transformQueryObject(queryObject) {
      return Object.entries(queryObject).reduce((transformed, [key, value]) => {
        const { params } = this.query

        if (Array.isArray(params[key])) {
          transformed[key] = decodeURIComponent(value).split(',')
        } else if (this.queryTypes && this.queryTypes[key]) {
          transformed[key] = this.convertQueryValueType({ key, value })
        } else {
          transformed[key] = value
        }
        return transformed
      }, {})
    },
    filterArrBySearchQuery(arr, type = 'offer', key = 'name') {
      return arr.filter(({ [type]: { default: offerData } }) => {
        return (
          !offerData[key] ||
          !this.query.searchTerm ||
          offerData[key].toLowerCase().includes(this.query.searchTerm.toLowerCase())
        )
      })
    },
  },
}
